'use client';

import type { FC, PropsWithChildren } from 'react';
import { useEffect, useMemo, useState } from 'react';

import type { OfferData } from '@wwe-portal/entities/offer.type';

import { Each } from '@wwe-portal/ui/components/utils';
import { EmptySlot } from '@wwe-portal/ui/features/store-revamp';
import { getDateTimeWithZone } from '@wwe-portal/utils/date';
import { groupBy } from '@wwe-portal/ui/lib/group-by';
import { cn } from '@wwe-portal/ui/lib/lib';

import { tryGetOfferCardType } from '../lib/mapper';
import { OfferCardTypeEnum } from '@wwe-portal/entities/offer.type';

import { maxItemsByType } from './helper';
import { OfferCard } from './offer-card';
import { useOfferContext } from './offer-provider';
import { StoreRevampBanner } from './store-revamp-banner';

const MAX_TIMEOUT_INTERVAL = 3600 * 1000;

const CellType = {
  Offer: 'Offer',
  StepupBanner: 'StepupBanner',
} as const;
export interface Banner {
  endDate: string;
  description: string;
  bannerUrl: string;
}
export interface OfferGridProps {
  offers: OfferData[];
  banner?: Banner;
}

interface CellProps {
  type: keyof typeof CellType;
  cellType: OfferCardTypeEnum;
  offer?: OfferData;
  banner?: Banner;
}

const getOfferEndDateTime = (offerData: OfferData) => {
  if (!offerData.EndDate) return null;

  return getDateTimeWithZone(new Date(offerData.EndDate), 'UTC', {
    keepLocalTime: true,
  }).getTime();
};

const isOfferExpired = (now: Date, offerData: OfferData) => {
  const endDateTime = getOfferEndDateTime(offerData);
  if (endDateTime == null || isNaN(endDateTime)) {
    return false;
  }

  return endDateTime <= now.getTime();
};

const useIsOfferExpired = (offerData: OfferData) => {
  const [isExpired, setIsExpired] = useState(
    isOfferExpired(new Date(), offerData)
  );

  useEffect(() => {
    const endDateTime = getOfferEndDateTime(offerData);
    const now = new Date();

    // If offer doesn't contain endDate value, do nothing
    if (
      endDateTime == null ||
      isNaN(endDateTime) ||
      isOfferExpired(now, offerData)
    )
      return;
    const remainingTime = endDateTime - now.getTime();
    if (remainingTime >= MAX_TIMEOUT_INTERVAL) return;

    // If offer doesn't contain endDate value, do nothing
    const timeout = setTimeout(
      () => {
        setIsExpired(true);
      },
      Math.max(remainingTime, 0)
    );
    return () => {
      clearTimeout(timeout);
    };
  }, [offerData]);
  return isExpired;
};

const OfferCardConsumer: FC<{ offer: OfferData }> = ({ offer }) => {
  const { offerToProps, onOfferClick, onOfferPriceClick } = useOfferContext();
  const isExpired = useIsOfferExpired(offer);

  const props = useMemo(() => {
    return offerToProps(offer);
  }, [offerToProps, offer]);

  if (isExpired && !offer?.isDailyOffer)
    return <EmptySlot type={offer.CombinationType as never} />;

  return (
    <OfferCard
      {...props}
      onClick={(e) => {
        onOfferClick(offer, e);
      }}
      onPurchaseClick={(e) => {
        onOfferPriceClick(offer, e);
      }}
    />
  );
};

const CellTypeWrapper: FC<
  PropsWithChildren<{ cellType: OfferCardTypeEnum }>
> = ({ cellType, children }) => {
  return (
    <div
      className={cn('relative', {
        'aspect-single-1': cellType === OfferCardTypeEnum.SINGLE1,
        'aspect-single-2': cellType === OfferCardTypeEnum.SINGLE2,
        'aspect-single-3': cellType === OfferCardTypeEnum.SINGLE3,
      })}
    >
      <div className="absolute inset-0">{children}</div>
    </div>
  );
};

export const StepupBannerConsumer: FC<Banner> = ({
  endDate,
  bannerUrl,
  description,
}) => {
  const { onBannerClick, onBannerMoreClick } = useOfferContext();
  return (
    <CellTypeWrapper cellType={OfferCardTypeEnum.SINGLE1}>
      <StoreRevampBanner
        banner={bannerUrl}
        description={description}
        endDate={endDate}
        onBannerClickMore={onBannerMoreClick}
        onClick={onBannerClick}
      />
    </CellTypeWrapper>
  );
};

const Cell: FC<CellProps> = ({ type, cellType, offer, banner }) => {
  if (type === 'StepupBanner' && banner != null) {
    return (
      <StepupBannerConsumer
        bannerUrl={banner.bannerUrl}
        description={banner.description}
        endDate={banner.endDate}
      />
    );
  }
  if (type === 'Offer' && offer != null) {
    return <OfferCardConsumer offer={offer} />;
  }
  return (
    <CellTypeWrapper cellType={cellType}>
      <EmptySlot type={cellType} />
    </CellTypeWrapper>
  );
};

interface CellData {
  type: keyof typeof CellType;
  cellType: OfferCardTypeEnum;
  offer?: OfferData;
  banner?: Banner;
}

export const OfferGrid: FC<OfferGridProps> = ({ offers, banner }) => {
  const groups = useMemo(() => {
    let cells: CellData[] = offers.map((offer) => ({
      type: CellType.Offer,
      cellType: tryGetOfferCardType(offer),
      offer,
    }));
    if (banner) {
      cells = [
        {
          type: CellType.StepupBanner,
          cellType: OfferCardTypeEnum.SINGLE1,
          banner,
        },
        ...cells,
      ];
    }
    const returedGroups = groupBy(
      cells,
      (cell) => cell.cellType,
      (type) => maxItemsByType[type]
    );
    returedGroups.forEach((group) => {
      const n = maxItemsByType[group.type];
      if (group.children.length < n) {
        const emptyCells: CellData[] = new Array<CellData>(
          n - group.children.length
        ).fill({
          type: CellType.Offer,
          cellType: group.type,
        });

        group.children.push(...emptyCells);
      }
    });
    return returedGroups;
  }, [offers, banner]);

  return (
    <div className="3xl:grid-cols-[repeat(18,_minmax(0,_1fr))] grid grid-cols-6 gap-[12px] md:grid-cols-12 xl:grid-cols-12">
      <Each
        data={groups}
        render={(item, index) => (
          <Each
            key={index}
            data={item.children}
            render={(cell, cellIndex) => (
              <div
                key={cellIndex}
                className={cn({
                  'col-span-2': maxItemsByType[item.type] === 3,
                  'col-span-3': maxItemsByType[item.type] === 2,
                  'col-span-6': maxItemsByType[item.type] === 1,
                })}
              >
                <Cell {...cell} />
              </div>
            )}
          />
        )}
      />
    </div>
  );
};
